/**
 * CompletedCard — collapsed "recently completed" card.
 *
 * Keeps the dashboard focused on open work. Count + deep link to the filings
 * list filtered to "complete". Zero state swaps to a neutral phrasing rather
 * than the card disappearing, so returning owners can see their past work
 * lives somewhere.
 */

import React from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";

import { Card } from "@/components/shared/ui/card";

interface CompletedCardProps {
  count: number;
  /** Year this snapshot is scoped to; shown in the secondary label. */
  calendarYear: number;
  /** Optional destination for the "View all" link. Sensible default. */
  linkTo?: string;
}

export const CompletedCard: React.FC<CompletedCardProps> = ({
  count,
  calendarYear,
  linkTo = "/buildings",
}) => (
  <Card density="default" className="gap-0">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <span
          className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-success/10 [color:hsl(var(--success))]"
          aria-hidden="true"
        >
          <CheckCircle2 className="h-5 w-5" />
        </span>
        <div>
          <p className="text-sm font-semibold text-foreground">
            Recently completed
          </p>
          <p className="text-xs text-muted-foreground tabular-nums">
            {count} filing{count === 1 ? "" : "s"} accepted or exempt &middot;
            CY{calendarYear}
          </p>
        </div>
      </div>
      <Link
        to={linkTo}
        className="inline-flex items-center gap-1 rounded-sm px-1 text-sm font-medium text-foreground hover:text-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        View all
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  </Card>
);

export default CompletedCard;
